import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, BarChart, Bar, Legend, ReferenceLine } from 'recharts';
import { TrendingUp, Calendar, Loader2, Percent, BarChart3, AlertTriangle, Target, Activity, DollarSign, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
interface PaperTradingPeriod {
  id: string;
  period_name: string;
  start_date: string;
  end_date: string;
  starting_capital: number;
  total_return_pct: number | null;
  sharpe_ratio: number | null;
  sortino_ratio: number | null;
}
interface PaperTradingTrade {
  id: string;
  period_id: string;
  entry_date: string;
  exit_date: string | null;
  stock_symbol: string;
  option_type: string;
  strike_price: number;
  expiration_date: string;
  result_pct: number | null;
  result_dollars: number | null;
}

// Helper function to format date string (YYYY-MM-DD) for display without timezone shift
const formatDateForDisplay = (dateStr: string, options?: Intl.DateTimeFormatOptions): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', options);
};

// Number of trades to show before collapsing
const INITIAL_TRADES_SHOWN = 5;
const PaperTradingResults = () => {
  const [periods, setPeriods] = useState<PaperTradingPeriod[]>([]);
  const [allTrades, setAllTrades] = useState<Record<string, PaperTradingTrade[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedPeriods, setExpandedPeriods] = useState<Record<string, boolean>>({});
  useEffect(() => {
    fetchData();
  }, []);
  const fetchData = async () => {
    try {
      // Fetch periods
      const {
        data: periodsData,
        error: periodsError
      } = await supabase.from('paper_trading_periods').select('*').order('start_date', {
        ascending: true
      });
      if (periodsError) throw periodsError;
      setPeriods(periodsData || []);

      // Fetch all trades
      const {
        data: tradesData,
        error: tradesError
      } = await supabase.from('paper_trading_trades').select('*').order('entry_date', {
        ascending: true
      });
      if (tradesError) throw tradesError;

      // Group trades by period
      const tradesByPeriod: Record<string, PaperTradingTrade[]> = {};
      (tradesData || []).forEach(trade => {
        if (!tradesByPeriod[trade.period_id]) {
          tradesByPeriod[trade.period_id] = [];
        }
        tradesByPeriod[trade.period_id].push(trade);
      });
      setAllTrades(tradesByPeriod);
    } catch (error) {
      console.error('Error fetching paper trading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate cumulative return data for a period with daily benchmark comparison
  const generateCumulativeData = (periodId: string, period: PaperTradingPeriod) => {
    const trades = allTrades[periodId] || [];
    if (trades.length === 0) return [];
    
    // S&P 500 historical average ~10.5% annual = ~0.0417% per trading day
    const SP500_ANNUAL_RETURN = 10.5;
    const TRADING_DAYS_PER_YEAR = 252;
    const avgDailyBenchmarkReturn = SP500_ANNUAL_RETURN / TRADING_DAYS_PER_YEAR;
    
    // Get all unique trading days from trades, sorted by date
    const tradingDays = trades
      .filter(t => t.result_dollars !== null)
      .map(t => t.entry_date)
      .filter((date, index, self) => self.indexOf(date) === index)
      .sort();
    
    const data: {
      date: string;
      cumulative: number;
      benchmark: number;
    }[] = [];
    
    let cumulativePnLDollars = 0;
    
    tradingDays.forEach((day, dayIndex) => {
      // Sum all trades for this day
      const dayTrades = trades.filter(t => t.entry_date === day && t.result_dollars !== null);
      dayTrades.forEach(trade => {
        cumulativePnLDollars += trade.result_dollars!;
      });
      
      const cumulativeReturnPct = (cumulativePnLDollars / period.starting_capital) * 100;
      // Cumulative benchmark return compounds daily
      const benchmarkCumulative = avgDailyBenchmarkReturn * (dayIndex + 1);
      
      data.push({
        date: formatDateForDisplay(day, {
          month: 'short',
          day: 'numeric'
        }),
        cumulative: parseFloat(cumulativeReturnPct.toFixed(2)),
        benchmark: parseFloat(benchmarkCumulative.toFixed(2))
      });
    });
    
    return data;
  };

  // Generate daily performance data for a period
  const generateDailyData = (periodId: string, period: PaperTradingPeriod) => {
    const trades = allTrades[periodId] || [];
    if (trades.length === 0) return [];

    // Each trade represents a daily return
    return trades.filter(trade => trade.result_dollars !== null).map(trade => ({
      date: formatDateForDisplay(trade.entry_date, {
        month: 'short',
        day: 'numeric'
      }),
      return: parseFloat((trade.result_dollars! / period.starting_capital * 100).toFixed(2))
    }));
  };

  // Calculate total return from trades: (sum of result_dollars) / starting_capital * 100
  const calculateTotalReturn = (periodId: string, startingCapital: number): number => {
    const trades = allTrades[periodId] || [];
    if (trades.length === 0 || startingCapital <= 0) return 0;
    let totalPnLDollars = 0;
    trades.forEach(trade => {
      if (trade.result_dollars !== null) {
        totalPnLDollars += trade.result_dollars;
      }
    });
    return totalPnLDollars / startingCapital * 100;
  };

  // Calculate average daily return
  const getAverageDailyReturn = (periodId: string, period: PaperTradingPeriod) => {
    const trades = allTrades[periodId] || [];
    const tradingDays = trades.filter(t => t.result_dollars !== null).length;
    if (tradingDays === 0) return '0.00';
    const totalReturn = calculateTotalReturn(periodId, period.starting_capital);
    return (totalReturn / tradingDays).toFixed(2);
  };

  // Calculate extrapolated annual return (252 trading days)
  const getAnnualizedReturn = (periodId: string, period: PaperTradingPeriod) => {
    const trades = allTrades[periodId] || [];
    const tradingDays = trades.filter(t => t.result_dollars !== null).length;
    if (tradingDays === 0) return {
      pct: '0.0',
      dollars: 0
    };
    const totalReturn = calculateTotalReturn(periodId, period.starting_capital);
    const annualized = totalReturn / tradingDays * 252;
    const annualizedDollars = annualized / 100 * period.starting_capital;
    return {
      pct: annualized.toFixed(1),
      dollars: annualizedDollars
    };
  };

  // Calculate extrapolated monthly return (21 trading days)
  const getMonthlyReturn = (periodId: string, period: PaperTradingPeriod) => {
    const trades = allTrades[periodId] || [];
    const tradingDays = trades.filter(t => t.result_dollars !== null).length;
    if (tradingDays === 0) return {
      pct: '0.0',
      dollars: 0
    };
    const totalReturn = calculateTotalReturn(periodId, period.starting_capital);
    const monthly = totalReturn / tradingDays * 21;
    const monthlyDollars = monthly / 100 * period.starting_capital;
    return {
      pct: monthly.toFixed(2),
      dollars: monthlyDollars
    };
  };

  // Calculate win rate
  const getWinRate = (periodId: string) => {
    const trades = allTrades[periodId] || [];
    if (trades.length === 0) return 'N/A';
    const wins = trades.filter(t => (t.result_pct || 0) > 0).length;
    return (wins / trades.length * 100).toFixed(0) + '%';
  };

  // Get benchmark return for the period (S&P 500 historical average)
  const getBenchmarkReturn = (periodId: string, period: PaperTradingPeriod) => {
    const trades = allTrades[periodId] || [];
    const tradingDays = trades.filter(t => t.result_dollars !== null).length;
    // S&P 500 historical average ~10.5% annual = ~0.0417% per trading day
    const SP500_DAILY_RETURN = 10.5 / 252;
    const benchmarkPct = SP500_DAILY_RETURN * tradingDays;
    const benchmarkDollars = benchmarkPct / 100 * period.starting_capital;
    return {
      pct: benchmarkPct.toFixed(2),
      dollars: benchmarkDollars
    };
  };

  // Calculate total PnL in dollars
  const getTotalPnLDollars = (periodId: string) => {
    const trades = allTrades[periodId] || [];
    return trades.reduce((sum, t) => sum + (t.result_dollars || 0), 0);
  };

  // Calculate final account value
  const getFinalAccountValue = (periodId: string, startingCapital: number) => {
    const totalPnL = getTotalPnLDollars(periodId);
    return startingCapital + totalPnL;
  };

  // Calculate average % of account at risk (approximation based on trade size)
  // Since we don't have position size data, we'll estimate based on average absolute daily return
  const getAvgAccountAtRisk = (periodId: string, period: PaperTradingPeriod) => {
    const trades = allTrades[periodId] || [];
    const completedTrades = trades.filter(t => t.result_dollars !== null);
    if (completedTrades.length === 0) return 'N/A';

    // Average absolute P/L per trade as a proxy for position size at risk
    const avgAbsolutePnL = completedTrades.reduce((sum, t) => sum + Math.abs(t.result_dollars || 0), 0) / completedTrades.length;
    // Estimate position size assuming average 15% option price movement
    const estimatedPositionSize = avgAbsolutePnL / 0.15;
    const pctAtRisk = estimatedPositionSize / period.starting_capital * 100;
    return pctAtRisk.toFixed(1) + '%';
  };

  // Calculate Sharpe Ratio from trades data
  // Sharpe = (Average Return - Risk-Free Rate) / Standard Deviation of Returns
  const calculateSharpeRatio = (periodId: string, period: PaperTradingPeriod): number | null => {
    const trades = allTrades[periodId] || [];
    const dailyReturns = trades
      .filter(t => t.result_dollars !== null)
      .map(t => (t.result_dollars! / period.starting_capital) * 100);
    
    if (dailyReturns.length < 2) return null;
    
    const avgReturn = dailyReturns.reduce((sum, r) => sum + r, 0) / dailyReturns.length;
    const riskFreeDaily = 5.0 / 252; // ~5% annual risk-free rate
    
    const variance = dailyReturns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / dailyReturns.length;
    const stdDev = Math.sqrt(variance);
    
    if (stdDev === 0) return null;
    
    // Annualized Sharpe Ratio
    const dailySharpe = (avgReturn - riskFreeDaily) / stdDev;
    return dailySharpe * Math.sqrt(252);
  };

  // Calculate Sortino Ratio from trades data
  // Sortino = (Average Return - Risk-Free Rate) / Downside Deviation
  const calculateSortinoRatio = (periodId: string, period: PaperTradingPeriod): number | null => {
    const trades = allTrades[periodId] || [];
    const dailyReturns = trades
      .filter(t => t.result_dollars !== null)
      .map(t => (t.result_dollars! / period.starting_capital) * 100);
    
    if (dailyReturns.length < 2) return null;
    
    const avgReturn = dailyReturns.reduce((sum, r) => sum + r, 0) / dailyReturns.length;
    const riskFreeDaily = 5.0 / 252; // ~5% annual risk-free rate
    
    // Only consider negative returns for downside deviation
    const negativeReturns = dailyReturns.filter(r => r < 0);
    if (negativeReturns.length === 0) return null; // No downside = infinite Sortino (return null instead)
    
    const downsideVariance = dailyReturns.reduce((sum, r) => {
      const deviation = Math.min(0, r - riskFreeDaily);
      return sum + Math.pow(deviation, 2);
    }, 0) / dailyReturns.length;
    
    const downsideDeviation = Math.sqrt(downsideVariance);
    
    if (downsideDeviation === 0) return null;
    
    // Annualized Sortino Ratio
    const dailySortino = (avgReturn - riskFreeDaily) / downsideDeviation;
    return dailySortino * Math.sqrt(252);
  };

  // Toggle expanded state for trade history
  const toggleExpanded = (periodId: string) => {
    setExpandedPeriods(prev => ({
      ...prev,
      [periodId]: !prev[periodId]
    }));
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
      </div>;
  }
  if (periods.length === 0) {
    return null; // Don't render anything if no paper trading periods exist
  }
  return <section className="py-16 border-t border-delphi-gray/20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">Verified Investor Trial Paper Trading Results</h2>
          <p className="text-delphi-light/70 text-lg mb-8">
            Paper trading results from investor trial periods, demonstrating live strategy performance with simulated capital. Potential investors were sent each trade in real-time as it was executed and independently verified these results throughout the trial period.
          </p>

          {periods.map(period => {
          const cumulativeData = generateCumulativeData(period.id, period);
          const dailyData = generateDailyData(period.id, period);
          const trades = allTrades[period.id] || [];
          const avgDailyReturn = getAverageDailyReturn(period.id, period);
          const annualizedReturn = getAnnualizedReturn(period.id, period);
          const monthlyReturn = getMonthlyReturn(period.id, period);
          const winRate = getWinRate(period.id);
          const benchmark = getBenchmarkReturn(period.id, period);
          const totalPnLDollars = getTotalPnLDollars(period.id);
          const finalAccountValue = getFinalAccountValue(period.id, period.starting_capital);
          const avgAtRisk = getAvgAccountAtRisk(period.id, period);
          const isExpanded = expandedPeriods[period.id] || false;
          // Calculate total return from trades data (not from stored value)
          const totalReturnPct = calculateTotalReturn(period.id, period.starting_capital);
          // Calculate Sharpe and Sortino ratios dynamically
          const sharpeRatio = calculateSharpeRatio(period.id, period);
          const sortinoRatio = calculateSortinoRatio(period.id, period);
          return <div key={period.id} className="mb-16 last:mb-0">
                <div className="relative bg-gradient-to-br from-delphi-red/10 via-transparent to-delphi-red/5 backdrop-blur-sm p-[2px] rounded-2xl mb-6 shadow-[0_0_20px_rgba(234,56,76,0.15)]">
                  <div className="absolute inset-0 rounded-2xl border-2 border-delphi-red/40 pointer-events-none" />
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-delphi-red/50 via-delphi-red/20 to-delphi-red/50 opacity-60 blur-[1px] pointer-events-none" />
                  <div className="relative bg-delphi-dark border border-delphi-red/30 rounded-xl p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-semibold flex items-center text-delphi-light">
                          <Calendar size={20} className="text-delphi-blue mr-2" />
                          {period.period_name}
                        </h3>
                        <p className="text-delphi-light/60 text-sm mt-1">
                          {formatDateForDisplay(period.start_date, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })} - {formatDateForDisplay(period.end_date, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                        </p>
                      </div>
                      <div className="mt-4 md:mt-0 text-left md:text-right">
                        <p className={`text-2xl font-bold ${totalReturnPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {`${totalReturnPct >= 0 ? '+' : ''}${totalReturnPct.toFixed(2)}%`}
                          <span className="text-lg ml-2">
                            ({totalPnLDollars >= 0 ? '+' : ''}${totalPnLDollars.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })})
                          </span>
                        </p>
                        <p className="text-delphi-light/60 text-sm">Total Return</p>
                      </div>
                    </div>

                    {/* Summary Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6 p-4 bg-delphi-gray/10 rounded-lg">
                      <div>
                        <p className="text-sm text-delphi-light/60">Final Account Value</p>
                        <p className="text-xl font-bold text-delphi-light">${finalAccountValue.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}</p>
                      </div>
                      <div>
                        <p className="text-sm text-delphi-light/60">Monthly (Extrapolated)</p>
                        <p className={`text-xl font-bold ${parseFloat(monthlyReturn.pct) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {parseFloat(monthlyReturn.pct) >= 0 ? '+' : ''}{monthlyReturn.pct}%
                          <span className="text-sm ml-1">(${monthlyReturn.dollars.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })})</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-delphi-light/60">Annual (Extrapolated)</p>
                        <p className={`text-xl font-bold ${parseFloat(annualizedReturn.pct) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {parseFloat(annualizedReturn.pct) >= 0 ? '+' : ''}{annualizedReturn.pct}%
                          <span className="text-sm ml-1">(${annualizedReturn.dollars.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })})</span>
                        </p>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-delphi-gray/20 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <Activity size={16} className="text-delphi-blue mr-2" />
                          <span className="text-sm text-delphi-light/60">Avg Daily</span>
                        </div>
                        <p className="text-xl font-bold text-delphi-light">{avgDailyReturn}%</p>
                      </div>

                      <div className="bg-delphi-gray/20 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <BarChart3 size={16} className="text-delphi-blue mr-2" />
                          <span className="text-sm text-delphi-light/60">Win Rate</span>
                        </div>
                        <p className="text-xl font-bold text-delphi-light">{winRate}</p>
                      </div>

                      <div className="bg-delphi-gray/20 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <Shield size={16} className="text-delphi-blue mr-2" />
                          <span className="text-sm text-delphi-light/60">Avg Position Size</span>
                        </div>
                        <p className="text-xl font-bold text-delphi-light">{avgAtRisk}</p>
                      </div>

                      <div className="bg-delphi-gray/20 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <Target size={16} className="text-delphi-blue mr-2" />
                          <span className="text-sm text-delphi-light/60">S&P 500 (Same Period)</span>
                        </div>
                        <p className="text-xl font-bold text-delphi-light/70">
                          +{benchmark.pct}%
                          <span className="text-sm ml-1">(${benchmark.dollars.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })})</span>
                        </p>
                      </div>

                      {sharpeRatio !== null && <div className="bg-delphi-gray/20 rounded-lg p-4">
                          <div className="flex items-center mb-2">
                            <Percent size={16} className="text-delphi-blue mr-2" />
                            <span className="text-sm text-delphi-light/60">Sharpe Ratio</span>
                          </div>
                          <p className={`text-xl font-bold ${sharpeRatio >= 1 ? 'text-green-400' : sharpeRatio >= 0 ? 'text-delphi-light' : 'text-red-400'}`}>{sharpeRatio.toFixed(2)}</p>
                        </div>}

                      {sortinoRatio !== null && <div className="bg-delphi-gray/20 rounded-lg p-4">
                          <div className="flex items-center mb-2">
                            <AlertTriangle size={16} className="text-delphi-blue mr-2" />
                            <span className="text-sm text-delphi-light/60">Sortino Ratio</span>
                          </div>
                          <p className={`text-xl font-bold ${sortinoRatio >= 1.5 ? 'text-green-400' : sortinoRatio >= 0 ? 'text-delphi-light' : 'text-red-400'}`}>{sortinoRatio.toFixed(2)}</p>
                        </div>}

                      <div className="bg-delphi-gray/20 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <DollarSign size={16} className="text-delphi-blue mr-2" />
                          <span className="text-sm text-delphi-light/60">Starting Capital</span>
                        </div>
                        <p className="text-xl font-bold text-delphi-light">${period.starting_capital.toLocaleString()}</p>
                      </div>

                      <div className="bg-delphi-gray/20 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <Calendar size={16} className="text-delphi-blue mr-2" />
                          <span className="text-sm text-delphi-light/60">Trading Days</span>
                        </div>
                        <p className="text-xl font-bold text-delphi-light">{trades.filter(t => t.result_dollars !== null).length}</p>
                      </div>
                    </div>

                    {/* Cumulative Return Chart with Benchmark */}
                    {cumulativeData.length > 0 && <div className="mb-6">
                        <h4 className="text-lg font-medium text-delphi-light mb-4 flex items-center">
                          <TrendingUp size={18} className="text-delphi-blue mr-2" />
                          Cumulative Return vs S&P 500 (%)
                        </h4>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={cumulativeData} margin={{
                        top: 5,
                        right: 30,
                        left: 0,
                        bottom: 5
                      }}>
                              <defs>
                                <pattern id={`diagonalLines-${period.id}`} patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
                                  <line x1="0" y1="0" x2="0" y2="4" stroke="#3b82f6" strokeWidth="1" strokeOpacity="0.2" />
                                </pattern>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                              <XAxis dataKey="date" stroke="#8E9196" tick={{
                          fontSize: 12
                        }} />
                              <YAxis stroke="#8E9196" />
                              <Tooltip contentStyle={{
                          backgroundColor: '#1A1F2C',
                          borderColor: '#2a2a2a',
                          color: '#ffffff'
                        }} formatter={(value: number, name: string) => [`${value.toFixed(2)}%`, name === 'cumulative' ? 'Strategy' : 'S&P 500']} />
                              <Legend formatter={value => value === 'cumulative' ? 'Strategy' : 'S&P 500'} wrapperStyle={{
                          color: '#8E9196'
                        }} />
                              <ReferenceLine y={0} stroke="#8E9196" strokeDasharray="3 3" />
                              <Area type="monotone" dataKey="cumulative" stroke="none" fill={`url(#diagonalLines-${period.id})`} />
                              <Line type="monotone" dataKey="cumulative" name="cumulative" stroke="#3b82f6" strokeWidth={3} dot={{
                          r: 4,
                          fill: '#3b82f6'
                        }} activeDot={{
                          r: 6
                        }} />
                              <Line type="monotone" dataKey="benchmark" name="benchmark" stroke="#8E9196" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>}

                    {/* Daily Performance Bar Chart */}
                    {dailyData.length > 1 && <div className="mb-6">
                        <h4 className="text-lg font-medium text-delphi-light mb-4 flex items-center">
                          <Activity size={18} className="text-delphi-blue mr-2" />
                          Daily Performance (%)
                        </h4>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dailyData} margin={{
                        top: 5,
                        right: 30,
                        left: 0,
                        bottom: 5
                      }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                              <XAxis dataKey="date" stroke="#8E9196" tick={{
                          fontSize: 10
                        }} interval={Math.floor(dailyData.length / 10)} />
                              <YAxis stroke="#8E9196" />
                              <Tooltip contentStyle={{
                          backgroundColor: '#1A1F2C',
                          borderColor: '#2a2a2a',
                          color: '#ffffff'
                        }} formatter={(value: number) => [`${value.toFixed(2)}%`, 'Daily Return']} />
                              <ReferenceLine y={0} stroke="#8E9196" strokeDasharray="3 3" />
                              <Bar dataKey="return" name="Daily Return" fill="#3b82f6" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>}

                    {/* Trade History Table - Collapsible */}
                    {trades.length > 0 && <div>
                        <h4 className="text-lg font-medium text-delphi-light mb-4">Trade History ({trades.length} trades)</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-delphi-gray/30">
                                <th className="text-left py-3 text-delphi-light/60 font-medium">Date</th>
                                <th className="text-left py-3 text-delphi-light/60 font-medium">Ticker</th>
                                <th className="text-left py-3 text-delphi-light/60 font-medium">Type</th>
                                <th className="text-left py-3 text-delphi-light/60 font-medium">Strike</th>
                                <th className="text-left py-3 text-delphi-light/60 font-medium">Exp</th>
                                <th className="text-right py-3 text-delphi-light/60 font-medium">Return (%)</th>
                                <th className="text-right py-3 text-delphi-light/60 font-medium">P/L ($)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Always show first INITIAL_TRADES_SHOWN trades */}
                              {trades.slice(0, INITIAL_TRADES_SHOWN).map(trade => <tr key={trade.id} className="border-b border-delphi-gray/10 hover:bg-delphi-gray/10">
                                  <td className="py-3 text-delphi-light/80">
                                    {formatDateForDisplay(trade.entry_date, {
                              month: 'short',
                              day: 'numeric'
                            })}
                                  </td>
                                  <td className="py-3 text-delphi-light font-medium">{trade.stock_symbol}</td>
                                  <td className={`py-3 ${trade.option_type === 'call' ? 'text-green-400' : 'text-red-400'}`}>
                                    {trade.option_type.toUpperCase()}
                                  </td>
                                  <td className="py-3 text-delphi-light/80">${trade.strike_price}</td>
                                  <td className="py-3 text-delphi-light/60">
                                    {formatDateForDisplay(trade.expiration_date, {
                              month: 'short',
                              day: 'numeric'
                            })}
                                  </td>
                                  <td className={`py-3 text-right font-medium ${(trade.result_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {trade.result_pct !== null ? `${trade.result_pct >= 0 ? '+' : ''}${trade.result_pct.toFixed(2)}%` : '-'}
                                  </td>
                                  <td className={`py-3 text-right font-medium ${(trade.result_dollars || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {trade.result_dollars !== null ? `${trade.result_dollars >= 0 ? '+' : ''}$${Math.abs(trade.result_dollars).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })}` : '-'}
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                          
                          {/* Show fade effect and expand button if more trades exist */}
                          {trades.length > INITIAL_TRADES_SHOWN && !isExpanded && <div className="relative">
                              <div className="absolute inset-x-0 -top-12 h-12 bg-gradient-to-t from-delphi-dark to-transparent pointer-events-none" />
                              <Button variant="ghost" onClick={() => toggleExpanded(period.id)} className="w-full mt-2 text-delphi-blue hover:text-delphi-blue/80 hover:bg-delphi-gray/20">
                                <ChevronDown size={18} className="mr-2" />
                                Show all {trades.length} trades
                              </Button>
                            </div>}
                          
                          {/* Expanded trades */}
                          {isExpanded && trades.length > INITIAL_TRADES_SHOWN && <>
                              <table className="w-full text-sm">
                                <tbody>
                                  {trades.slice(INITIAL_TRADES_SHOWN).map(trade => <tr key={trade.id} className="border-b border-delphi-gray/10 hover:bg-delphi-gray/10">
                                      <td className="py-3 text-delphi-light/80" style={{
                              width: '14.28%'
                            }}>
                                        {formatDateForDisplay(trade.entry_date, {
                                month: 'short',
                                day: 'numeric'
                              })}
                                      </td>
                                      <td className="py-3 text-delphi-light font-medium" style={{
                              width: '14.28%'
                            }}>{trade.stock_symbol}</td>
                                      <td className={`py-3 ${trade.option_type === 'call' ? 'text-green-400' : 'text-red-400'}`} style={{
                              width: '14.28%'
                            }}>
                                        {trade.option_type.toUpperCase()}
                                      </td>
                                      <td className="py-3 text-delphi-light/80" style={{
                              width: '14.28%'
                            }}>${trade.strike_price}</td>
                                      <td className="py-3 text-delphi-light/60" style={{
                              width: '14.28%'
                            }}>
                                        {formatDateForDisplay(trade.expiration_date, {
                                month: 'short',
                                day: 'numeric'
                              })}
                                      </td>
                                      <td className={`py-3 text-right font-medium ${(trade.result_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{
                              width: '14.28%'
                            }}>
                                        {trade.result_pct !== null ? `${trade.result_pct >= 0 ? '+' : ''}${trade.result_pct.toFixed(2)}%` : '-'}
                                      </td>
                                      <td className={`py-3 text-right font-medium ${(trade.result_dollars || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{
                              width: '14.28%'
                            }}>
                                        {trade.result_dollars !== null ? `${trade.result_dollars >= 0 ? '+' : ''}$${Math.abs(trade.result_dollars).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              })}` : '-'}
                                      </td>
                                    </tr>)}
                                </tbody>
                              </table>
                              <Button variant="ghost" onClick={() => toggleExpanded(period.id)} className="w-full mt-2 text-delphi-blue hover:text-delphi-blue/80 hover:bg-delphi-gray/20">
                                <ChevronUp size={18} className="mr-2" />
                                Show less
                              </Button>
                            </>}
                        </div>
                      </div>}
                  </div>
                </div>
              </div>;
        })}

          <p className="text-delphi-light/60 text-sm italic mt-4">
            Note: Paper trading results represent simulated trading with no actual capital at risk. 
            Past paper trading performance is not indicative of future real-money results.
          </p>
        </div>
      </div>
    </section>;
};
export default PaperTradingResults;